import gql from 'graphql-tag';
import { useMutation, useQuery } from '@apollo/react-hooks';

import { useCurrentAccountId } from '@zapier/promotions/src/hooks';
import { isOfferExpired } from '@zapier/promotions/src/utils';

import { offerQuery as OfferQueryResult } from './types/offerQuery';
import { getOfferEndDate } from './getOfferEndDate';

const offerVariantFragment = gql`
  fragment offerVariantFragment on OfferVariant {
    id
    campaign {
      id
      maxUses
      name
      slug
    }
    destinationPlan {
      amount
      displayShortName
      id
      interval
      name
    }
    promotionPlanGroup {
      id
      name
    }
    isControl
    monthlyCost
    name
    terms
    totalCost
  }
`;

const offerQuery = gql`
  query offerQuery($accountId: Int!, $offerId: ID!) {
    account(id: $accountId) {
      id
      activeOfferId
      offer(offerId: $offerId) {
        id
        acceptedAt
        accountId
        createdAt
        expiry
        variant {
          ...offerVariantFragment
        }
      }
      promotionPlanGroupId
    }
  }
  ${offerVariantFragment}
`;

const acceptOfferMutation = gql`
  mutation acceptOffer($input: AcceptOfferInput!) {
    acceptOffer(input: $input) {
      acceptedAt
      targetPlanId
      variant {
        ...offerVariantFragment
      }
    }
  }
  ${offerVariantFragment}
`;

export type UseOfferProps = {
  offerId: string | null | undefined;
  onAcceptOfferSuccess?: () => void;
};

const defaultProps = {
  onAcceptOfferSuccess: () => {},
};

export const useOffer = ({
  offerId,
  onAcceptOfferSuccess,
}: Partial<UseOfferProps> = defaultProps) => {
  const { accountId } = useCurrentAccountId();

  const queryResult = useQuery<OfferQueryResult>(offerQuery, {
    skip: !offerId || !accountId,
    variables: {
      accountId,
      offerId,
    },
  });

  const [acceptOffer, mutationResult] = useMutation(acceptOfferMutation, {
    onCompleted: onAcceptOfferSuccess,
  });

  const offer = queryResult?.data?.account?.offer;
  const activeOfferId = queryResult?.data?.account?.activeOfferId;

  const onAcceptOffer = (targetPlanId?: string) => {
    acceptOffer({
      variables: {
        input: {
          acceptedAt: new Date(),
          accountId,
          offerId: offer?.id,
          targetPlanId,
          variant: offer?.variant,
        },
      },
    });
  };

  const offerAcceptedOrExpired =
    offer?.acceptedAt || isOfferExpired(offer?.expiry);
  const offerFoundForAccount = !queryResult?.loading && !!offer;

  return {
    acceptOffer: onAcceptOffer,
    offer,
    offerEndDate: getOfferEndDate({
      acceptedAt: offer?.acceptedAt,
      maxUses: offer?.variant?.campaign?.maxUses,
    }),
    error: mutationResult?.error
      ? "We've run into an issue applying this offer. Please try again."
      : undefined,
    isLoading: queryResult?.loading,
    isOfferActive: activeOfferId && activeOfferId === offer?.id,
    isOfferAvailable: Boolean(offerFoundForAccount && !offerAcceptedOrExpired),
    isSaving: mutationResult.loading,
  };
};
